import { locale, tx } from '../i18n';
import { num } from './values';
import type { CategoryName, ConfidenceLevel, VerdictLabel } from '../types/api';

/*
 * Formatters are built per call: the language can change while the page is open.
 *
 * currencyDisplay is forced to the symbol because these are British prices whatever the reader's
 * language: pl-PL would otherwise write "171,39 GBP", which reads as a conversion rather than the
 * amount on the bill. The grouping and decimal marks still follow the reader's locale.
 */
function gbp(precise: boolean): Intl.NumberFormat {
  return new Intl.NumberFormat(locale(), {
    style: 'currency',
    currency: 'GBP',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: precise ? 2 : 0,
    maximumFractionDigits: precise ? 2 : 0,
  });
}

/** What every formatter writes when it is handed nothing to format. */
export function notPublished(): string {
  return tx('Not published', 'Yayımlanmamış', 'Nieopublikowane', 'Não publicado', 'No publicado');
}

/** Pence to pounds, rounded; used for prices. */
export function formatPence(pence: number | null | undefined): string {
  const pennies = num(pence);
  if (pennies === null) {
    return notPublished();
  }
  const pounds = pennies / 100;
  return gbp(!Number.isInteger(pounds)).format(pounds);
}

/** Whole pounds. */
export function formatPounds(pounds: number | null | undefined): string {
  const value = num(pounds);
  return value === null ? notPublished() : gbp(false).format(value);
}

export function formatNumber(value: number | null | undefined): string {
  const finite = num(value);
  return finite === null ? notPublished() : new Intl.NumberFormat(locale()).format(finite);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return tx('Not published', 'Yayımlanmamış',
    'Nieopublikowane',
    'Não publicado',
    'No publicado');
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? tx('Not published', 'Yayımlanmamış',
    'Nieopublikowane',
    'Não publicado',
    'No publicado')
    : new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(parsed);
}

/**
 * True when a "valid to" date is behind us.
 *
 * <p>Compared against the start of today, not the current instant. The register's date is the last
 * day a certificate is valid, so one that says "valid to 3 October" is valid all through the
 * third - and {@code Date.parse} on an ISO date gives midnight UTC, which is already behind
 * {@code Date.now()} by breakfast. Measuring from midnight is the difference between a correct
 * answer and one that calls a live certificate expired for a whole day.
 *
 * <p>Here rather than in a component because two screens ask it: the report's energy section and
 * the address list's chip. They must never disagree about whether the same certificate has lapsed.
 */
export function hasLapsed(validTo: string | null | undefined): boolean {
  if (!validTo) {
    return false;
  }
  const expiry = Date.parse(validTo);
  if (Number.isNaN(expiry)) {
    return false;
  }
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return expiry < midnight.getTime();
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : new Intl.DateTimeFormat(locale(), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsed);
}

/** English regions as the API names them (ONS), in the reader's language. */
export function regionLabel(onsName: string): string {
  const names: Record<string, [string, string, string, string]> = {
    London: ['Londra', 'Londyn', 'Londres', 'Londres'],
    'South East': [
      'Güney Doğu İngiltere', 'Południowy wschód', 'Sudeste de Inglaterra',
      'Sureste de Inglaterra',
    ],
    'East of England': [
      'Doğu İngiltere', 'Wschodnia Anglia', 'Leste de Inglaterra', 'Este de Inglaterra',
    ],
    'South West': [
      'Güney Batı İngiltere', 'Południowy zachód', 'Sudoeste de Inglaterra',
      'Suroeste de Inglaterra',
    ],
    'West Midlands': ['Batı Midlands', 'West Midlands', 'West Midlands', 'West Midlands'],
    'East Midlands': ['Doğu Midlands', 'East Midlands', 'East Midlands', 'East Midlands'],
    'Yorkshire and The Humber': [
      'Yorkshire ve Humber', 'Yorkshire i Humber', 'Yorkshire e Humber', 'Yorkshire y Humber',
    ],
    'North West': [
      'Kuzey Batı İngiltere', 'Północny zachód', 'Noroeste de Inglaterra',
      'Noroeste de Inglaterra',
    ],
    'North East': [
      'Kuzey Doğu İngiltere', 'Północny wschód', 'Nordeste de Inglaterra',
      'Noreste de Inglaterra',
    ],
  };
  const known = names[onsName];
  return known ? tx(onsName, ...known) : onsName;
}

/**
 * How a percentile should be read aloud. {@code area} is the region it was measured against
 * (from the category's metrics); without it the sentence says "its region".
 */
export function describePercentile(
  percentile: number | null | undefined,
  area?: string | null,
): string | null {
  // Not `!== null`: the server omits a null property altogether, so this arrives as undefined.
  const rank = num(percentile);
  if (rank === null) {
    return null;
  }
  const place = area ? regionLabel(area) : null;
  const en = place ?? 'its region';
  const tr = place ? `${place} genelinin` : 'bölgesinin';
  if (rank >= 50) {
    return tx(`Better than ${rank}% of ${en}`, `${tr} yüzde ${rank} kadarından iyi`,
    `Lepiej niż ${rank}% ${en}`,
    `Melhor do que ${rank}% de ${en}`,
    `Mejor que el ${rank}% de ${en}`);
  }
  return tx(`Only better than ${rank}% of ${en}`, `${tr} yalnızca yüzde ${rank} kadarından iyi`,
    `Tylko lepiej niż ${rank}% ${en}`,
    `Apenas melhor do que ${rank}% de ${en}`,
    `Solo mejor que el ${rank}% de ${en}`);
}

export const VERDICT_TONE: Record<VerdictLabel, 'good' | 'mixed' | 'bad'> = {
  EXCELLENT: 'good',
  GOOD: 'good',
  FAIR: 'mixed',
  POOR: 'bad',
};

export function verdictCopy(label: VerdictLabel): string {
  switch (label) {
    case 'EXCELLENT':
      return tx('Excellent', 'Mükemmel', 'Doskonały', 'Excelente', 'Excelente');
    case 'GOOD':
      return tx('Good', 'İyi', 'Dobry', 'Bom', 'Bueno');
    case 'FAIR':
      return tx('Fair', 'Orta', 'Przeciętny', 'Razoável', 'Aceptable');
    default:
      return tx('Poor', 'Zayıf', 'Słaby', 'Fraco', 'Deficiente');
  }
}

export function confidenceCopy(level: ConfidenceLevel): string {
  switch (level) {
    case 'HIGH':
      return tx('High confidence', 'Yüksek güven', 'Wysoka pewność', 'Confiança alta', 'Confianza alta');
    case 'MEDIUM':
      return tx('Medium confidence', 'Orta güven', 'Średnia pewność', 'Confiança média', 'Confianza media');
    default:
      return tx('Low confidence', 'Düşük güven', 'Niska pewność', 'Confiança baixa', 'Confianza baja');
  }
}

export function categoryBlurb(category: CategoryName): string {
  switch (category) {
    case 'SAFETY':
      return tx('Recorded crime within 500m over 12 months', '12 ayda 500 m içindeki kayıtlı suçlar',
        'Odnotowana przestępczość w promieniu 500 m w ciągu 12 miesięcy',
        'Crime registado num raio de 500 m em 12 meses',
        'Delincuencia registrada en 500 m en 12 meses');
    case 'COMFORT':
      return tx('Energy performance and running costs', 'Enerji performansı ve giderler',
        'Charakterystyka energetyczna i koszty eksploatacji',
        'Desempenho energético e custos de utilização',
        'Eficiencia energética y costes de la vivienda');
    case 'CONNECTIVITY':
      return tx('Fixed broadband available at this postcode', 'Bu posta kodundaki sabit internet altyapısı',
        'Internet stacjonarny dostępny pod tym kodem pocztowym',
        'Internet fixa disponível neste código postal',
        'Internet fija disponible en este código postal');
    default:
      return tx('Licensing and enforcement history nearby', 'Çevredeki lisans ve yaptırım geçmişi',
        'Historia licencji i postępowań w okolicy',
        'Histórico de licenças e coimas nas imediações',
        'Historial de licencias y sanciones en la zona');
  }
}

/** Colour for a 0-100 score, matching the verdict palette. */
export function scoreTone(score: number): 'good' | 'mixed' | 'bad' {
  if (score >= 70) {
    return 'good';
  }
  return score >= 55 ? 'mixed' : 'bad';
}

/**
 * The brand status colours as literal values, for the few places that cannot take a CSS variable:
 * an SVG presentation attribute is one. These are the darkened -ink values from the stylesheet,
 * which clear 4.5:1 as text and 3:1 as a stroke, so one value is safe in both roles.
 */
export function toneColour(tone: 'good' | 'mixed' | 'bad'): string {
  switch (tone) {
    case 'good':
      return '#0b6254';
    case 'mixed':
      return '#7a4e06';
    default:
      return '#a32020';
  }
}
